import React, { useState, useEffect } from 'react';
import Dialog from '../common/Dialog';
import { useDispatch, useSelector } from 'react-redux';
import { createCostGroup, fetchCostGroupsData, fetchCostCodesData, deleteCostCodeItem } from '../../store/slices/proposalsSlice';
import { PencilIcon, EyeIcon, EyeSlashIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { showToast } from '../utils/Toast';

const AddScopeDialog = ({ isOpen, onClose, onSave, proposalId }) => {
  const dispatch = useDispatch();
  const costGroupsData = useSelector(state => state.proposals.costGroupsData);
  const costCodesData = useSelector(state => state.proposals.costCodesData);
  const loading = useSelector(state => state.proposals.loading);
  const [isEditing, setIsEditing] = useState(false);
  const [showNumberFields, setShowNumberFields] = useState(false);
  const [showCostCodes, setShowCostCodes] = useState(false);
  const [costCodeSearchTerm, setCostCodeSearchTerm] = useState('');
  const [selectedCostCodes, setSelectedCostCodes] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    builderCost: 0,
    markup: 49.25,
    clientPrice: 0,
    margin: 33
  });

  // Reset form data and state when dialog opens
  useEffect(() => {
    if (isOpen) {
      setFormData({
        name: '',
        description: '',
        builderCost: 0,
        markup: 49.25,
        clientPrice: 0,
        margin: 33
      });
      setIsEditing(false);
      setShowNumberFields(false);
      setShowCostCodes(false);
      setCostCodeSearchTerm('');
      setSelectedCostCodes([]);
    }
  }, [isOpen]);

  useEffect(() => {
    dispatch(fetchCostGroupsData());
    dispatch(fetchCostCodesData());
  }, [dispatch]);

  // Update group totals when selected cost codes change
  useEffect(() => {
    if (selectedCostCodes.length > 0) {
      const totalBuilderCost = selectedCostCodes.reduce((sum, code) => sum + Number(code.builderCost || 0), 0);
      const totalClientPrice = selectedCostCodes.reduce((sum, code) => sum + Number(code.clientPrice || 0), 0);
      
      setFormData(prev => ({
        ...prev,
        builderCost: totalBuilderCost,
        clientPrice: totalClientPrice,
        margin: calculateMargin(totalBuilderCost, totalClientPrice)
      }));
    }
  }, [selectedCostCodes]);

  const labelClasses = "block text-xs uppercase text-gray-500 mb-1";
  const inputClasses = `
    w-full border border-gray-200 px-4 py-2.5 
    focus:outline-none focus:border-blue-500 text-gray-900
    bg-white
  `;
  const textAreaClasses = `
    w-full border border-gray-200 px-4 py-2.5 
    focus:outline-none focus:border-blue-500 text-gray-900
    bg-white resize-none h-24
  `;

  const calculateBuilderCost = (quantity, unitCost) => {
    const qty = Number(quantity) || 0;
    const cost = Number(unitCost) || 0;
    return qty * cost;
  };

  const calculateClientPrice = (builderCost, markup) => {
    const cost = Number(builderCost) || 0;
    const markupPercent = Number(markup) || 49.25;
    return cost * (1 + (markupPercent / 100));
  };

  const calculateMargin = (builderCost, clientPrice) => {
    const cost = Number(builderCost) || 0;
    const price = Number(clientPrice) || 0;
    
    if (cost <= 0 || price <= 0) return 0;
    return ((price - cost) / price * 100).toFixed(2);
  };

  const handleChange = (field, value) => {
    const updates = { [field]: value };
    
    if (field === 'builderCost' || field === 'markup') {
      const builderCost = field === 'builderCost' ? Number(value) : Number(formData.builderCost);
      const markup = field === 'markup' ? Number(value) : Number(formData.markup);
      
      // Calculate client price based on builder cost and markup
      updates.clientPrice = calculateClientPrice(builderCost, markup);
      updates.margin = calculateMargin(builderCost, updates.clientPrice);
    } else if (field === 'clientPrice') {
      const clientPrice = Number(value);
      const builderCost = Number(formData.builderCost);
      
      // Update margin when client price changes
      updates.margin = calculateMargin(builderCost, clientPrice);
    }

    setFormData(prev => ({
      ...prev,
      ...updates
    }));
  };

  const handleCostGroupSelect = (groupId) => {
    const selectedGroup = costGroupsData.find(group => group.groupId === groupId);
    if (selectedGroup) {
      setFormData(prev => ({
        ...prev,
        name: selectedGroup.title,
        description: selectedGroup.description || ''
      }));
    }
  };

  const handleToggleCostCode = (costCode) => {
    setSelectedCostCodes(prev => {
      // Check if cost code is already selected (by ID)
      const existingIndex = prev.findIndex(item => item._id === costCode._id);
      
      if (existingIndex >= 0) {
        // If selected, remove it
        return prev.filter(item => item._id !== costCode._id);
      } else {
        // If not selected, add it with all its properties
        // Calculate proper values if needed
        const builderCost = calculateBuilderCost(costCode.quantity || 1, costCode.unitCost || 0);
        let clientPrice = costCode.clientPrice;
        
        // If client price is missing, calculate it from builder cost and markup
        if (!clientPrice || clientPrice <= 0) {
          clientPrice = calculateClientPrice(builderCost, costCode.markup || 49.25);
        }
        
        // Calculate margin from builder cost and client price
        const margin = calculateMargin(builderCost, clientPrice);
        
        return [...prev, {
          _id: costCode._id,
          item: costCode.item,
          description: costCode.description || '',
          quantity: costCode.quantity || 1,
          unit: costCode.unit !== undefined && costCode.unit !== '' ? costCode.unit : 'EA',
          unitCost: costCode.unitCost || 0,
          builderCost: builderCost,
          markup: costCode.markup || 49.25,
          clientPrice: clientPrice,
          margin: margin
        }];
      }
    });
  };

  // Filter cost codes based on search term
  const filteredCostCodes = costCodesData.filter(code => 
    code.item?.toLowerCase().includes(costCodeSearchTerm.toLowerCase()) ||
    code.description?.toLowerCase().includes(costCodeSearchTerm.toLowerCase())
  );

  // Handle removal of selected cost code
  const handleRemoveSelectedCostCode = async (code) => {
    // If editing an existing group and the cost code has an ID,
    // and it's an existing item from the database, delete it
    if (code._id && proposalId) {
      try {
        await dispatch(deleteCostCodeItem(code._id)).unwrap();
        // Remove from selected cost codes state
        setSelectedCostCodes(prev => prev.filter(item => item._id !== code._id));
      } catch (error) {
        console.error('Failed to delete cost code:', error);
        showToast('Failed to delete cost code: ' + error.message, 'error');
      }
    } else {
      // Just remove from the local state
      setSelectedCostCodes(prev => prev.filter(item => 
        item._id !== code._id || 
        (item.item !== code.item && item.description !== code.description)
      ));
    }
  };

  const handleSubmit = async () => {
    try {
      // Create the cost group first
      const groupData = {
        ...formData,
        proposal: proposalId,
        markup: formData.markup || 49.25,
        margin: formData.margin || 33
      };

      console.log('Submitting cost group data:', groupData);
      
      const result = await dispatch(createCostGroup(groupData)).unwrap();
      console.log('Cost group created successfully, response:', result);
      
      // Save the full cost code objects with all their properties
      const resultWithCostCodes = {
        ...result,
        selectedCostCodes: selectedCostCodes.length > 0 ? selectedCostCodes : []
      };
      
      onSave(resultWithCostCodes);
      onClose();
    } catch (error) {
      console.error('Failed to create scope of work:', error);
      // Add more detailed error logging
      if (error.response) {
        console.error('Error response:', error.response);
      }
      showToast(`Failed to create scope of work: ${error.message || error}`, 'error');
    }
  };

  // Calculate the total of selected cost codes for display
  const calculateSelectedCodesTotal = () => {
    return selectedCostCodes.reduce((total, code) => total + (Number(code.clientPrice) || 0), 0);
  };

  return (
    <Dialog isOpen={isOpen} onClose={onClose}>
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold">Add New Scope of Work</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="space-y-4 overflow-y-auto max-h-[60vh]">
          <div>
            <label className={labelClasses}>Name</label>
            <div className="flex items-center gap-2">
              {isEditing ? (
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  className={inputClasses}
                  placeholder="Enter scope name"
                />
              ) : (
                <select
                  value={formData.name ? costGroupsData.find(group => group.title === formData.name)?.groupId : ''}
                  onChange={(e) => handleCostGroupSelect(e.target.value)}
                  className={inputClasses}
                >
                  <option value="">Select a scope</option>
                  {costGroupsData?.map(group => (
                    <option key={group.groupId} value={group.groupId}>
                      {group.title}
                    </option>
                  ))}
                </select>
              )}
              <button
                onClick={() => setIsEditing(!isEditing)}
                className="p-2 text-gray-500 hover:text-gray-700"
              >
                <PencilIcon className="w-5 h-5" />
              </button>
            </div>
          </div>

          <div>
            <label className={labelClasses}>Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              className={textAreaClasses}
              placeholder="Enter description"
              rows={3}
            />
          </div>

          <div className="flex justify-between mb-2">
            {/* <button
              onClick={() => setShowNumberFields(!showNumberFields)}
              className="flex items-center gap-1 text-sm text-gray-400 hover:text-gray-600 transition-colors"
            >
              {showNumberFields ? (
                <>
                  <EyeSlashIcon className="w-4 h-4" />
                  Hide Cost Details
                </>
              ) : (
                <>
                  <EyeIcon className="w-4 h-4" />
                  Show Cost Details
                </>
              )}
            </button> */}
            
            <button
              onClick={() => setShowCostCodes(!showCostCodes)}
              className="flex items-center gap-1 text-sm text-blue-500 hover:text-blue-700 transition-colors"
            >
              {showCostCodes ? "Hide Cost Codes" : "Add Cost Codes to Group"}
            </button>
          </div>

          {showNumberFields && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={labelClasses}>Builder Cost</label>
                <input
                  type="number"
                  value={formData.builderCost}
                  onChange={(e) => handleChange('builderCost', e.target.value)}
                  className={inputClasses}
                  placeholder="0.00"
                />
              </div>

              <div>
                <label className={labelClasses}>Markup %</label>
                <input
                  type="number"
                  value={formData.markup}
                  onChange={(e) => handleChange('markup', e.target.value)}
                  className={inputClasses}
                  placeholder="0"
                  step="any"
                />
              </div>

              <div>
                <label className={labelClasses}>Client Price</label>
                <input
                  type="number"
                  value={formData.clientPrice}
                  onChange={(e) => handleChange('clientPrice', e.target.value)}
                  className={inputClasses}
                  placeholder="0.00"
                />
              </div>

              <div>
                <label className={labelClasses}>Margin %</label>
                <input
                  type="number"
                  value={formData.margin}
                  className={`${inputClasses} bg-gray-50`}
                  readOnly
                />
              </div>
            </div>
          )}

          {/* Cost code selection section */}
          {showCostCodes && (
            <div className="mt-4 border-t pt-4">
              <div className="flex justify-between items-center">
                <h3 className="text-sm font-medium mb-2">Select Cost Codes to Include</h3>
                {selectedCostCodes.length > 0 && (
                  <div className="text-sm text-blue-600">
                    {selectedCostCodes.length} cost codes selected - Total: ${calculateSelectedCodesTotal().toFixed(2)}
                  </div>
                )}
              </div>
              
              {/* Search for cost codes */}
              <div className="relative mb-3">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  className={`${inputClasses} pl-10`}
                  placeholder="Search cost codes..."
                  value={costCodeSearchTerm}
                  onChange={(e) => setCostCodeSearchTerm(e.target.value)}
                />
              </div>
              
              {/* Cost code selection list */}
              <div className="max-h-60 overflow-y-auto border border-gray-200 rounded">
                {loading ? (
                  <div className="p-4 text-center text-gray-500">Loading cost codes...</div>
                ) : filteredCostCodes.length === 0 ? (
                  <div className="p-4 text-center text-gray-500">No cost codes found</div>
                ) : (
                  <ul className="divide-y divide-gray-200">
                    {filteredCostCodes.map(code => {
                      // Calculate the builder cost and client price for display
                      const builderCost = calculateBuilderCost(code.quantity || 1, code.unitCost || 0);
                      const clientPrice = code.clientPrice || calculateClientPrice(builderCost, code.markup || 49.25);
                      
                      return (
                        <li key={code._id} className="flex items-center p-3 hover:bg-gray-50">
                          <input
                            type="checkbox"
                            id={`costcode-${code._id}`}
                            checked={selectedCostCodes.some(item => item._id === code._id)}
                            onChange={() => handleToggleCostCode(code)}
                            className="mr-3 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                          />
                          <label 
                            htmlFor={`costcode-${code._id}`}
                            className="flex-1 flex flex-col cursor-pointer"
                          >
                            <span className="font-medium text-gray-900">{code.item}</span>
                            {code.description && (
                              <span className="text-sm text-gray-500 truncate">{code.description}</span>
                            )}
                          </label>
                          <div className="text-right">
                            <div className="text-gray-500">${clientPrice.toFixed(2)}</div>
                            <div className="text-xs text-gray-400">{code.quantity || 1} × ${code.unitCost || 0}</div>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
              
              {/* Selected cost codes preview */}
              {selectedCostCodes.length > 0 && (
                <div className="mt-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Selected Cost Codes</h4>
                  <div className="border border-blue-100 rounded-md p-2 bg-blue-50 max-h-40 overflow-y-auto">
                    <ul className="divide-y divide-blue-200">
                      {selectedCostCodes.map((code, index) => (
                        <li key={index} className="py-1 flex justify-between items-center">
                          <div className="flex-1">
                            <div className="text-sm font-medium">{code.item}</div>
                            <div className="text-xs text-gray-500 flex gap-2">
                              <span>{code.quantity} {code.unit}</span>
                              <span>${code.clientPrice?.toFixed(2)}</span>
                            </div>
                          </div>
                          <button 
                            onClick={() => handleRemoveSelectedCostCode(code)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
          >
            Add to Proposal
          </button>
        </div>
      </div>
    </Dialog>
  );
};

export default AddScopeDialog; 